import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Trophy, Calendar, Table2, ArrowLeft, CheckCircle, Clock, 
  MapPin, Play, Timer, ChevronRight, Users, Download, Loader2, Camera, X, Image
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";
import html2canvas from "html2canvas";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-HN", { weekday: "short", day: "numeric", month: "short" });
};

const STATUS_LABELS = {
  scheduled: { label: "Programado", icon: Calendar, color: "bg-blue-100 text-blue-700" },
  live: { label: "En Vivo", icon: Play, color: "bg-red-100 text-red-700 animate-pulse" },
  finished: { label: "Finalizado", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  suspended: { label: "Suspendido", icon: Timer, color: "bg-yellow-100 text-yellow-700" },
  postponed: { label: "Aplazado", icon: Clock, color: "bg-gray-100 text-gray-700" },
};

// Photo Gallery Modal Component
function MatchPhotoGallery({ match, isOpen, onClose }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  if (!isOpen || !match) return null;
  
  const photos = match.photos || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Fotos del Partido
          </DialogTitle>
        </DialogHeader>
        
        {/* Match Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              {match.home_team?.logo_url && (
                <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
              )}
              <span className="font-medium">{match.home_team?.name}</span>
            </div>
            <span className="text-xl font-bold">
              {match.home_score ?? 0} - {match.away_score ?? 0}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-medium">{match.away_team?.name}</span>
              {match.away_team?.logo_url && (
                <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
              )}
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            {formatDate(match.match_date)} {match.stadium && `• ${match.stadium}`}
          </p>
        </div>
        
        {/* Photos Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div 
                key={index} 
                className="aspect-square cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={getImageUrl(photo.url)}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No hay fotos disponibles</p>
          </div>
        )}
        
        {/* Full Image Preview */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={getImageUrl(selectedPhoto.url)}
              alt="Vista completa"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Match Row Component
function MatchRow({ match, onViewPhotos }) {
  const statusInfo = STATUS_LABELS[match.status] || STATUS_LABELS.scheduled;
  const hasPhotos = match.photos && match.photos.length > 0;
  
  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4 flex-1">
        {/* Home Team */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <span className="font-medium text-right">{match.home_team?.name}</span>
          {match.home_team?.logo_url && (
            <img 
              src={getImageUrl(match.home_team.logo_url)} 
              alt={match.home_team.name}
              className="w-8 h-8 object-contain"
            />
          )}
        </div>
        
        {/* Score */}
        <div className="flex items-center justify-center min-w-[80px]">
          {match.status === "finished" || match.status === "live" ? (
            <div className="flex items-center gap-1 font-bold text-xl">
              <span>{match.home_score ?? 0}</span>
              <span className="text-gray-400">-</span>
              <span>{match.away_score ?? 0}</span>
            </div>
          ) : (
            <span className="text-gray-500">{match.match_time || "vs"}</span>
          )}
        </div>
        
        {/* Away Team */}
        <div className="flex items-center gap-2 flex-1">
          {match.away_team?.logo_url && (
            <img 
              src={getImageUrl(match.away_team.logo_url)} 
              alt={match.away_team.name}
              className="w-8 h-8 object-contain"
            />
          )}
          <span className="font-medium">{match.away_team?.name}</span>
        </div>
      </div>
      
      <div className="flex items-center gap-3 ml-4">
        {hasPhotos && (
          <button
            onClick={() => onViewPhotos(match)}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors bg-primary/10 px-2 py-1 rounded-lg"
            title={`Ver ${match.photos.length} foto(s)`}
          >
            <Camera className="h-4 w-4" />
            <span className="text-xs font-medium">{match.photos.length}</span>
          </button>
        )}
        <Badge className={`${statusInfo.color} text-xs`}>
          {statusInfo.label}
        </Badge>
        <span className="text-xs text-gray-400 hidden sm:block">
          {formatDate(match.match_date)}
        </span>
      </div>
    </div>
  );
}

// Last 5 Results Component
function Last5Results({ results }) {
  if (!results || results.length === 0) return <span className="text-gray-400 text-xs">-</span>;
  
  return (
    <div className="flex items-center gap-1">
      {results.map((result, idx) => (
        <span
          key={idx}
          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
            result === 'W' 
              ? 'bg-green-500 text-white' 
              : result === 'D' 
                ? 'bg-gray-400 text-white' 
                : 'bg-red-500 text-white'
          }`}
          title={result === 'W' ? 'Ganado' : result === 'D' ? 'Empate' : 'Perdido'}
        >
          {result === 'W' ? '✓' : result === 'D' ? '−' : '✕'}
        </span>
      ))}
    </div>
  );
}

// Standings Table with Download Button
function StandingsTable({ standings, tournamentName }) {
  const tableRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);

  const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString("es-HN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleExportImage = async () => {
    if (!tableRef.current) return;
    
    setExporting(true);
    setShowWatermark(true);
    
    // Wait for watermark to render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const canvas = await html2canvas(tableRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `tabla-posiciones-${tournamentName?.toLowerCase().replace(/\s+/g, '-') || 'torneo'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error("Error exporting:", error);
    } finally {
      setShowWatermark(false);
      setExporting(false);
    }
  };

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay datos de tabla disponibles
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportImage}
          disabled={exporting}
          className="flex items-center gap-2"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Descargar como Imagen
        </Button>
      </div>
      
      <div ref={tableRef} className="overflow-x-auto bg-white rounded-lg p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-2 font-medium text-gray-600">#</th>
              <th className="text-left py-3 px-2 font-medium text-gray-600">Equipo</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">PJ</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">G</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">E</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">P</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">GF</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">GC</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">DG</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600 font-bold">PTS</th>
              <th className="text-center py-3 px-2 font-medium text-gray-600">Últ. 5</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((entry, idx) => (
              <tr 
                key={entry.team_id} 
                className={`border-b hover:bg-gray-50 ${idx < 4 ? "bg-green-50/50" : ""}`}
              >
                <td className="py-3 px-2 font-bold text-gray-600">{idx + 1}</td>
                <td className="py-3 px-2">
                  <Link 
                    to={`/deportes/equipo/${entry.team?.slug || entry.team_id}`}
                    className="flex items-center gap-2 hover:text-primary"
                  >
                    {entry.team?.logo_url && (
                      <img 
                        src={getImageUrl(entry.team.logo_url)} 
                        alt={entry.team.name}
                        className="w-6 h-6 object-contain"
                      />
                    )}
                    <span className="font-medium">{entry.team?.name || "Equipo"}</span>
                  </Link>
                </td>
                <td className="py-3 px-2 text-center">{entry.played}</td>
                <td className="py-3 px-2 text-center text-green-600">{entry.won}</td>
                <td className="py-3 px-2 text-center text-yellow-600">{entry.drawn}</td>
                <td className="py-3 px-2 text-center text-red-600">{entry.lost}</td>
                <td className="py-3 px-2 text-center">{entry.goals_for}</td>
                <td className="py-3 px-2 text-center">{entry.goals_against}</td>
                <td className="py-3 px-2 text-center font-medium">
                  {entry.goal_difference > 0 ? `+${entry.goal_difference}` : entry.goal_difference}
                </td>
                <td className="py-3 px-2 text-center font-bold text-primary">{entry.points}</td>
                <td className="py-3 px-2">
                  <Last5Results results={entry.last_5} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Watermark - Only visible when exporting */}
        {showWatermark && (
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-base font-semibold text-gray-700">{tournamentName}</p>
            <p className="text-sm text-gray-500 mt-1">{formatDateTime()}</p>
            <p className="text-sm font-medium text-primary mt-1">www.choluteca.net</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TorneoPage() {
  const { slug } = useParams();
  const [tournament, setTournament] = useState(null);
  const [matchdays, setMatchdays] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState(null);
  const [selectedMatchday, setSelectedMatchday] = useState("all");
  const [loading, setLoading] = useState(true);
  const [photoGalleryMatch, setPhotoGalleryMatch] = useState(null);

  useEffect(() => {
    fetchTournamentData();
  }, [slug]);

  const fetchTournamentData = async () => {
    try {
      const [tournamentRes, standingsRes] = await Promise.all([
        axios.get(`${API}/sports/tournaments/${slug}`),
        axios.get(`${API}/sports/standings/${slug}`),
      ]);
      
      if (!tournamentRes.data || !tournamentRes.data.tournament_id) {
        console.error("Tournament data invalid:", tournamentRes.data);
        setLoading(false);
        return;
      }
      
      setTournament(tournamentRes.data);
      setStandings(standingsRes.data);
      
      // Fetch matchdays and matches
      const matchdaysRes = await axios.get(`${API}/sports/matchdays?tournament_id=${tournamentRes.data.tournament_id}`);
      setMatchdays(matchdaysRes.data.items || []);
      
      const matchesRes = await axios.get(`${API}/sports/matches?tournament_id=${tournamentRes.data.tournament_id}&limit=500`);
      setMatches(matchesRes.data.items || []);
    } catch (error) {
      console.error("Error fetching tournament data:", error);
      console.error("Slug:", slug);
      console.error("API URL:", API);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = selectedMatchday === "all" 
    ? matches 
    : matches.filter(m => m.matchday_id === selectedMatchday);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!tournament) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <Trophy className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-medium text-gray-600 mb-2">Torneo no encontrado</h2>
          <Link to="/deportes">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Deportes
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section 
        className="relative py-16 md:py-24"
        style={{ backgroundColor: tournament.color || "#10B981" }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/deportes" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a Deportes
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-6"
          >
            {tournament.logo_url && (
              <img 
                src={getImageUrl(tournament.logo_url)} 
                alt={tournament.name}
                className="w-24 h-24 object-contain bg-white/20 rounded-xl p-3"
              />
            )}
            <div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                {tournament.name}
              </h1>
              <p className="text-white/80 text-lg">{tournament.season}</p>
              <div className="flex items-center gap-3 mt-3">
                <Badge className="bg-white/20 text-white border-0">
                  <Users className="h-3 w-3 mr-1" />
                  {tournament.teams?.length || tournament.team_ids?.length || 0} equipos
                </Badge>
                <Badge className={`${tournament.status === "active" ? "bg-green-500" : "bg-gray-500"} text-white border-0`}>
                  {tournament.status === "active" ? "En curso" : "Finalizado"}
                </Badge>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="standings" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="standings">Tabla</TabsTrigger>
              <TabsTrigger value="matches">Partidos</TabsTrigger>
              <TabsTrigger value="teams">Equipos</TabsTrigger>
            </TabsList>

            {/* Standings Tab */}
            <TabsContent value="standings">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                  <Table2 className="h-5 w-5 text-primary" />
                  Tabla de Posiciones
                </h2>
                <StandingsTable standings={standings?.standings} tournamentName={tournament.name} />
              </div>
            </TabsContent>

            {/* Matches Tab */}
            <TabsContent value="matches">
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-brand-text flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Partidos
                  </h2>
                  {matchdays.length > 0 && (
                    <select 
                      value={selectedMatchday}
                      onChange={(e) => setSelectedMatchday(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">Todas las jornadas</option>
                      {matchdays.map((md) => (
                        <option key={md.matchday_id} value={md.matchday_id}>
                          {md.name || `Jornada ${md.number}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                {filteredMatches.length > 0 ? (
                  <div className="space-y-3">
                    {filteredMatches.map((match) => (
                      <MatchRow key={match.match_id} match={match} onViewPhotos={setPhotoGalleryMatch} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay partidos programados
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Teams Tab */}
            <TabsContent value="teams">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Equipos Participantes
                </h2>
                
                {tournament.teams && tournament.teams.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {tournament.teams.map((team) => (
                      <Link 
                        key={team.team_id}
                        to={`/deportes/equipo/${team.slug}`}
                        className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        {team.logo_url ? (
                          <img 
                            src={getImageUrl(team.logo_url)} 
                            alt={team.name}
                            className="w-16 h-16 object-contain mb-2"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-full mb-2 flex items-center justify-center">
                            <Users className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                        <span className="font-medium text-sm text-center">{team.name}</span>
                        {team.city && (
                          <span className="text-xs text-gray-500">{team.city}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay equipos registrados
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      {/* Photo Gallery Modal */}
      <MatchPhotoGallery 
        match={photoGalleryMatch} 
        isOpen={!!photoGalleryMatch} 
        onClose={() => setPhotoGalleryMatch(null)} 
      />

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
